import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UploadSimple, Eye, Check, X, Camera, BellRinging, Trash } from "@phosphor-icons/react";

const BADGE = {
  DA_VALIDARE: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300",
  VALIDATO: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-300",
  RIFIUTATO: "bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-300",
};

export default function Certificates() {
  const { user } = useAuth();
  const canValidate = ["admin", "manager"].includes(user?.role);
  const isEmployee = user?.role === "employee";
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [shot, setShot] = useState("");

  useEffect(() => {
    if (!isEmployee) {
      api.get("/employees", { params: { status: "active" } }).then((r) => setEmployees(r.data)).catch(() => {});
    }
  }, [isEmployee]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/certificates", { params: { status } });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    const list = [...(fileRef.current?.files || [])];
    if (!list.length) { toast.error("Seleziona il file del certificato"); return; }
    if (!form.date_from) { toast.error("Indica il periodo di malattia"); return; }
    if (!isEmployee && !form.employee_id) { toast.error("Seleziona il dipendente"); return; }
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    fd.append("date_from", form.date_from);
    fd.append("date_to", form.date_to || form.date_from);
    fd.append("notes", form.notes || "");
    fd.append("employee_id", isEmployee ? "" : form.employee_id);
    setBusy(true);
    try {
      await api.post("/certificates", fd);
      toast.success(list.length > 1 ? `Certificato caricato (${list.length} pagine)` : "Certificato caricato");
      setForm(null);
      setShot("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const view = async (r, page = 0) => {
    try {
      const res = await api.get(`/certificates/${r.id}/file`, { params: { page }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const pages = r.pages_count || 1;
      setPreview({ url, cert: r, page, pages,
                   name: `${r.employee_name} — pagina ${page + 1} di ${pages}`,
                   isPdf: res.data.type?.includes("pdf") });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const apply = async (r) => {
    try {
      const { data } = await api.post(`/certificates/${r.id}/apply`);
      toast.success(`${data.days} giornate segnate come MALATTIA`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const reject = async (r) => {
    try { await api.patch(`/certificates/${r.id}/reject`); toast.success("Certificato rifiutato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const removeCert = async (r) => {
    if (!window.confirm(`Eliminare il certificato di ${r.employee_name}?`)) return;
    try { await api.delete(`/certificates/${r.id}`); toast.success("Certificato eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="certificates-page" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Certificati di Malattia</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isEmployee
              ? "Carica il tuo certificato: il responsabile lo visualizzerà e segnerà le giornate di malattia"
              : "Certificati caricati dai dipendenti: visualizza e segna le giornate di malattia"}
          </p>
        </div>
        <div className="mobile-stack flex flex-wrap gap-2">
          {canValidate && (
            <Button size="sm" variant="outline" className="rounded-sm font-semibold" data-testid="certificates-reminder-btn"
              onClick={async () => {
                try {
                  const { data } = await api.post("/certificates/reminder");
                  toast.success(data.pending
                    ? `Promemoria inviato per ${data.pending} certificati in attesa`
                    : data.message || "Nessun certificato in attesa da più di 2 giorni");
                } catch (e) { toast.error(errMsg(e)); }
              }}>
              <BellRinging size={15} className="mr-1.5" /> Promemoria da validare
            </Button>
          )}
          <Button size="sm" className="rounded-sm font-semibold" data-testid="add-certificate-btn"
            onClick={() => { setShot(""); setForm({ employee_id: "", date_from: "", date_to: "", notes: "" }); }}>
            <UploadSimple size={15} className="mr-1.5" /> Carica certificato
          </Button>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card p-3 flex gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[190px] rounded-sm" data-testid="certificate-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="DA_VALIDARE">Da validare</SelectItem>
            <SelectItem value="VALIDATO">Validati</SelectItem>
            <SelectItem value="RIFIUTATO">Rifiutati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm" data-testid="certificates-table">
          <thead className="bg-secondary">
            <tr>{["", "Dipendente", "Dal", "Al", "Giorni", "File", "Note", "Stato"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Caricamento...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground" data-testid="certificates-empty">Nessun certificato</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border hover:bg-secondary/40" data-testid={`certificate-row-${r.id}`}>
                <td className="px-3 py-1.5">
                  <div className="flex justify-start gap-1">
                    <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => view(r)} data-testid={`view-certificate-${r.id}`}>
                      <Eye size={14} className="mr-1" /> Apri{(r.pages_count || 1) > 1 ? ` (${r.pages_count} pag.)` : ""}
                    </Button>
                    {canValidate && r.status !== "VALIDATO" && (
                      <>
                        <Button size="sm" className="h-7 rounded-sm" onClick={() => apply(r)} data-testid={`apply-certificate-${r.id}`}>
                          <Check size={14} className="mr-1" /> Segna malattia
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-destructive" onClick={() => reject(r)} data-testid={`reject-certificate-${r.id}`}>
                          <X size={14} />
                        </Button>
                      </>
                    )}
                    {canValidate && (
                      <button type="button" title="Elimina certificato" data-testid={`delete-certificate-${r.id}`}
                        className="rounded-sm p-1.5 text-rose-600 hover:text-rose-700" onClick={() => removeCert(r)}>
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
                <td className="px-3 py-1.5">
                  <div className="font-semibold">{r.employee_name}</div>
                  <div className="text-[10px] text-muted-foreground tabular-nums">{r.employee_code} · {r.department}</div>
                </td>
                <td className="px-3 py-1.5 tabular-nums">{itDate(r.date_from)}</td>
                <td className="px-3 py-1.5 tabular-nums">{itDate(r.date_to)}</td>
                <td className="px-3 py-1.5 tabular-nums">{r.days}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-[180px] truncate">{r.original_filename}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-[180px] truncate">{r.notes || "—"}</td>
                <td className="px-3 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${BADGE[r.status]}`}>{r.status.replace("_", " ")}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <Dialog open onOpenChange={() => setForm(null)}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="certificate-dialog">
            <DialogHeader><DialogTitle className="font-head">Carica certificato di malattia</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {!isEmployee && (
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dipendente</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="h-9 rounded-sm mt-1" data-testid="certificate-employee"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name} ({e.code})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dal</Label>
                  <Input type="date" value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                    className="h-9 rounded-sm mt-1" data-testid="certificate-from" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Al</Label>
                  <Input type="date" value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                    className="h-9 rounded-sm mt-1" data-testid="certificate-to" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">File (PDF, PNG, JPG — max 10MB)</Label>
                <Input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" className="h-9 rounded-sm mt-1" data-testid="certificate-file" />
                <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
                  data-testid="certificate-camera-input"
                  onChange={(e) => { if (fileRef.current && e.target.files?.length) { fileRef.current.files = e.target.files; setShot(`${e.target.files.length} pagina/e`); } }} />
                <Button type="button" variant="outline" size="sm" className="mt-2 rounded-sm"
                  onClick={() => cameraRef.current?.click()} data-testid="certificate-camera-btn">
                  <Camera size={15} className="mr-1.5" /> Scatta foto col telefono
                </Button>
                <p className="mt-1 text-xs text-muted-foreground">
                  {shot ? `Selezionate: ${shot}` : "Puoi selezionare più pagine insieme (foto e/o PDF)."}
                </p>
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Note</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="h-9 rounded-sm mt-1" data-testid="certificate-notes" placeholder="Numero protocollo, medico..." />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setForm(null)} data-testid="certificate-cancel">Annulla</Button>
              <Button className="rounded-sm font-semibold" onClick={upload} disabled={busy} data-testid="certificate-upload">
                {busy ? "Caricamento..." : "Carica"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {preview && (
        <Dialog open onOpenChange={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}>
          <DialogContent className="rounded-sm sm:max-w-3xl" data-testid="certificate-preview">
            <DialogHeader><DialogTitle className="font-head text-base">{preview.name}</DialogTitle></DialogHeader>
            {preview.isPdf
              ? <iframe title="certificato" src={preview.url} className="w-full h-[70vh] border border-border rounded-sm" />
              : <img alt="certificato" src={preview.url} className="max-h-[70vh] w-auto mx-auto border border-border rounded-sm" />}
            <DialogFooter className="flex-wrap gap-2 sm:justify-between">
              {preview.pages > 1 && (
                <div className="mobile-stack flex flex-wrap items-center gap-2" data-testid="certificate-pages-nav">
                  {Array.from({ length: preview.pages }, (_, i) => (
                    <Button key={i} size="sm" variant={i === preview.page ? "default" : "outline"}
                      className="h-8 rounded-sm" data-testid={`certificate-page-${i + 1}`}
                      onClick={() => { URL.revokeObjectURL(preview.url); view(preview.cert, i); }}>
                      Pag. {i + 1}
                    </Button>
                  ))}
                </div>
              )}
              <a href={preview.url} download={preview.name} className="text-sm font-semibold text-primary hover:underline" data-testid="certificate-download">
                Scarica pagina
              </a>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
